import React, { useEffect, useRef, useState } from "react";
import { showToaster, toasterTypes } from "..";
import { MESSAGES } from "../../utils/Constants";

export default function VideoCaptureUpload(props: any) {
  const [video, setVideo] = useState<File | null>(null);     // single video
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [recording, setRecording] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordedChunks = useRef<Blob[]>([]);
  const fileInput = useRef<HTMLInputElement>(null);

  /* ───────── init camera once ───────── */
  useEffect(() => {
    if (props.isPreview) return;

    (async () => {
      try {
        const s = await navigator.mediaDevices.getUserMedia({ 
          video: true, 
          audio: true 
        });
        setStream(s);
        if (videoRef.current) videoRef.current.srcObject = s;
      } catch (e) {
        showToaster(toasterTypes.ERROR, "Couldn't access camera");
      }
    })();
    return () => stream?.getTracks().forEach(t => t.stop());
  }, [props.isPreview]);

  /* ───────── helpers ───────── */
  const startRecording = () => {
    if (!stream) return;
    recordedChunks.current = [];
    const mediaRecorder = new MediaRecorder(stream);
    
    mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        recordedChunks.current.push(event.data);
      }
    };

    mediaRecorder.onstop = () => {
      const blob = new Blob(recordedChunks.current, { type: "video/webm" });
      const videoFile = new File([blob], `recording_${Date.now()}.webm`, { type: "video/webm" });
      validateAndSave(videoFile);
    };

    mediaRecorderRef.current = mediaRecorder;
    mediaRecorder.start();
    setRecording(true);
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && recording) {
      mediaRecorderRef.current.stop();
      setRecording(false);
    }
  };

  const pickFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) validateAndSave(f);
  };

  const validateAndSave = (file: File) => {
    /* ---------- type guard ---------- */
    const allowed = props.acceptFiles?.split("|") ?? ["webm", "mp4", "mov", "avi"];
    const ext = file.type.split("/")[1] || file.name.split('.').pop()?.toLowerCase();
    if (!allowed.includes(ext)) {
      let list = allowed.join(", ").replace(/,([^,]*)$/, " and$1");             // a, b and c
      showToaster(
        toasterTypes.ERROR,
        props.messages?.acceptFiles || MESSAGES.ALLOWED_TYPES.replace("#{value}", list)
      );
      return;
    }

    /* ---------- size guard ---------- */
    if (props.maxSize && file.size > props.maxSize) {
      showToaster(
        toasterTypes.ERROR,
        props.messages?.maxSize || MESSAGES.MAX_SIZE.replace("#value", props.maxSize)
      );
      return;
    }

    /* ---------- total‑size guard (single video) ---------- */
    if (props.maxLimit && file.size > props.maxLimit) {
      const mb = (props.maxLimit / (1024 * 1024)).toFixed(2);
      showToaster(
        toasterTypes.ERROR,
        props.messages?.maxLimit || `Combined file size must not exceed ${mb} MB`
      );
      return;
    }
    
    setVideo(file);
    props.onUpload(file);                               // bubble up just like FileUpload
    showToaster(toasterTypes.SUCCESS, "Video selected!");
  };

  const removeVideo = (e: React.MouseEvent) => {
    setVideo(null);
    e.stopPropagation();
  };

  if (props?.value && props?.isPreview) {
    return (
      <div className="video-preview">
        <video 
          src={`https://go.coguide.in/c1/download/${props?.value}`} 
          controls 
          width="320" 
          height="240"
        />
      </div>
    );
  }
  
  if (!props?.value && props?.isPreview) {
    return (
      <div className="video-preview">
        <p>No Data</p>
      </div>
    );
  }

  /* ───────── render ───────── */
  return (
    <div className="custom-video-upload">
      <section className="upload-container">
        {video ? (
          <div className="preview-with-delete">
            <video 
              src={URL.createObjectURL(video)} 
              controls 
              width="320" 
              height="240"
            />
            <img
              src={`${process.env.PUBLIC_URL}/images/close_circle.svg`}
              onClick={removeVideo}
              alt="remove"
            />
          </div>
        ) : (
          <p>
            <span className="upload-placeholder">{props.placeholder}</span>
            <u className="browse-text" onClick={() => fileInput.current?.click()}>
              Browse Video
            </u>
          </p>
        )}

        {/* hidden file input for manual pick */}
        <input
          type="file"
          accept="video/*"
          ref={fileInput}
          style={{ display: "none" }}
          onChange={pickFile}
          disabled={props.isDisabled}
        />

        {/* camera view */}
        <div className="camera-wrapper">
          <video ref={videoRef} autoPlay playsInline muted width="320" height="240" />
          <div className="video-controls">
            {!recording ? (
              <button 
                disabled={props.isDisabled || !stream} 
                onClick={startRecording}
              >
                Start Recording
              </button>
            ) : (
              <button 
                disabled={props.isDisabled} 
                onClick={stopRecording}
              >
                Stop Recording
              </button>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}